// 控制台单连接实时总线（模块 04 控制台通道）：ticket 换取、退避重连、订阅集合（引用计数）、
// 指令 request_id ↔ ack 关联、租约心跳。页面不直接碰 WebSocket。

import type { Ack, Command, CommandOutcome, ConsoleMessage, ServerMessage } from "@primebot/proto-ts";
import { CLOSE_CODES, LIMITS, PROTOCOL_VERSION } from "@primebot/proto-ts";
import { create } from "zustand";
import { uuid } from "./utils";

export type BusStatus = "idle" | "connecting" | "open" | "closed";

export type AckResult = {
  robotId: string;
  requestId: string | null;
  seq: number;
  outcome: CommandOutcome;
  ack: Ack | null;
  /** 被拒时的错误码（如 `lease.required`、`cmd.estopped`） */
  error: string | null;
  /** 云端拒绝时的说明 */
  message: string | null;
};

export class CommandError extends Error {
  constructor(
    public code: string,
    public result?: AckResult,
  ) {
    super(code);
  }
}

type SocketLike = Pick<WebSocket, "send" | "close" | "readyState" | "bufferedAmount"> & {
  onopen: ((ev: Event) => void) | null;
  onclose: ((ev: CloseEvent) => void) | null;
  onmessage: ((ev: MessageEvent) => void) | null;
  onerror: ((ev: Event) => void) | null;
};

export type BusDeps = {
  getTicket: () => Promise<string>;
  openSocket: (url: string) => SocketLike;
  url: (ticket: string) => string;
  now?: () => number;
};

type Listener = (m: ServerMessage) => void;

const OPEN = 1;
/** 退避：0.5 s 起翻倍，封顶 10 s，加 ±20% 抖动 */
export function backoffMs(attempt: number, rand = Math.random): number {
  const base = Math.min(10_000, 500 * 2 ** Math.min(attempt, 10));
  return Math.round(base * (0.8 + rand() * 0.4));
}

/** 连接稳定这么久才把退避计数清零（避免握手后立刻被关的抖动风暴） */
const STABLE_MS = 10_000;
/** 回执等待：服务端 ack 超时 + 余量 */
const CMD_TIMEOUT_MS = LIMITS.ACK_TIMEOUT_MS + 2_000;
/** 订阅变更合并发送 */
const SUB_FLUSH_MS = 30;
/** 订阅被拒（限流）后重试的间隔 */
const SUB_RETRY_MS = 1000;
/** 发送缓冲超过这么多字节就不再发速度：上行拥塞时积压的旧速度会在松手后成批到达 */
const VELOCITY_MAX_BUFFERED = 4096;
/** 远程说话：发送缓冲积压超过约 0.5 s（24 kHz PCM16）就丢帧，免得网络慢时越积越晚 */
const TALK_MAX_BUFFERED = 24_000;

export const useBusStatus = create<{ status: BusStatus; lastClose: number | null }>(() => ({
  status: "idle",
  lastClose: null,
}));

export class RealtimeBus {
  private ws: SocketLike | null = null;
  private wanted = false;
  private attempt = 0;
  private openedAt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private subFlushTimer: ReturnType<typeof setTimeout> | null = null;
  /** robot_id → 引用计数 */
  private subs = new Map<string, number>();
  /** robot_id → 额外数据流（如 `proximity`）→ 引用计数 */
  private streams = new Map<string, Map<string, number>>();
  /** 已经告诉服务端的订阅：robot_id → 数据流集合（排序后逗号连接，空串 = 只有基本数据） */
  private sent = new Map<string, string>();
  private pending = new Map<
    string,
    { resolve: (r: AckResult) => void; reject: (e: Error) => void; timer: ReturnType<typeof setTimeout> }
  >();
  private listeners = new Set<Listener>();
  private statusListeners = new Set<(s: BusStatus) => void>();
  private gen = 0;

  constructor(private deps: BusDeps) {}

  get status(): BusStatus {
    return useBusStatus.getState().status;
  }

  private setStatus(status: BusStatus, code?: number) {
    useBusStatus.setState({ status, lastClose: code ?? useBusStatus.getState().lastClose });
    this.statusListeners.forEach((l) => l(status));
  }

  onStatus(l: (s: BusStatus) => void): () => void {
    this.statusListeners.add(l);
    return () => this.statusListeners.delete(l);
  }

  on(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  start() {
    if (this.wanted) return;
    this.wanted = true;
    this.attempt = 0;
    void this.connect();
  }

  /** 登出/页面卸载：关闭且不再重连；未完成的指令全部失败。 */
  stop() {
    this.wanted = false;
    this.gen++;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    const ws = this.ws;
    this.ws = null;
    if (ws) ws.close(1000, "bye");
    this.sent.clear();
    this.failPending("offline");
    this.setStatus("idle");
  }

  private async connect() {
    const gen = ++this.gen;
    // 只有真正打开过的连接才算"稳定"；握手阶段就失败（代理 4xx/5xx）不能清零退避
    this.openedAt = 0;
    this.setStatus("connecting");
    let ticket: string;
    try {
      ticket = await this.deps.getTicket();
    } catch {
      if (gen === this.gen) this.scheduleRetry();
      return;
    }
    if (gen !== this.gen || !this.wanted) return;
    const ws = this.deps.openSocket(this.deps.url(ticket));
    this.ws = ws;
    ws.onopen = () => {
      if (gen !== this.gen) return;
      this.openedAt = (this.deps.now ?? Date.now)();
      this.sent.clear();
      this.setStatus("open");
      this.flushSubs();
    };
    ws.onmessage = (ev) => {
      if (gen !== this.gen || typeof ev.data !== "string") return;
      let m: ServerMessage;
      try {
        m = JSON.parse(ev.data) as ServerMessage;
      } catch {
        return;
      }
      this.dispatch(m);
    };
    ws.onerror = () => {
      /* 随后必有 onclose */
    };
    ws.onclose = (ev) => {
      if (gen !== this.gen) return;
      this.ws = null;
      this.sent.clear();
      this.failPending("offline");
      this.setStatus("closed", ev.code);
      if (!this.wanted) return;
      if (this.openedAt && (this.deps.now ?? Date.now)() - this.openedAt > STABLE_MS) this.attempt = 0;
      // 4001（令牌失效）也照常重连：取 ticket 会走 REST 刷新，刷新失败则登出并 stop()
      this.scheduleRetry();
    };
  }

  private scheduleRetry() {
    if (!this.wanted) return;
    this.setStatus("closed");
    const delay = backoffMs(this.attempt++);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.wanted) void this.connect();
    }, delay);
  }

  private dispatch(m: ServerMessage) {
    if (m.t === "subscribe_result" && m.p.denied.length) {
      // 被拒（多半是订阅限流）的机器人还想要：作废"已告诉服务端"的记录，稍后重发（数据流变更不会就此丢掉）
      let retry = false;
      for (const id of m.p.denied) {
        if (this.subs.has(id) && this.sent.has(id)) {
          this.sent.delete(id);
          retry = true;
        }
      }
      if (retry && !this.subFlushTimer)
        this.subFlushTimer = setTimeout(() => {
          this.subFlushTimer = null;
          this.flushSubs();
        }, SUB_RETRY_MS);
    }
    if (m.t === "ack" && m.p.request_id) {
      const p = this.pending.get(m.p.request_id);
      if (p) {
        this.pending.delete(m.p.request_id);
        clearTimeout(p.timer);
        const r: AckResult = {
          robotId: m.p.robot_id,
          requestId: m.p.request_id,
          seq: m.p.seq,
          outcome: m.p.outcome,
          ack: m.p.ack,
          error: m.p.error,
          message: m.p.message,
        };
        if (r.outcome === "ok") p.resolve(r);
        else p.reject(new CommandError(r.error ?? r.outcome, r));
      }
    }
    this.listeners.forEach((l) => l(m));
  }

  private failPending(code: string) {
    for (const [, p] of this.pending) {
      clearTimeout(p.timer);
      p.reject(new CommandError(code));
    }
    this.pending.clear();
  }

  private raw(msg: ConsoleMessage): boolean {
    const ws = this.ws;
    if (!ws || ws.readyState !== OPEN) return false;
    ws.send(JSON.stringify({ v: PROTOCOL_VERSION, ts: Date.now(), ...msg }));
    return true;
  }

  // ---------------- 订阅 ----------------

  /**
   * 订阅一组机器人；返回取消函数（引用计数，多个页面可订阅同一台）。`streams` 是额外数据流（遥控台订
   * `proximity`），同样按引用计数；服务端收到的是每台机器人当前所有订阅者要的数据流的并集。
   */
  subscribe(ids: readonly string[], streams: readonly string[] = []): () => void {
    const uniq = [...new Set(ids)];
    const extra = [...new Set(streams)];
    for (const id of uniq) {
      this.subs.set(id, (this.subs.get(id) ?? 0) + 1);
      if (extra.length) {
        const m = this.streams.get(id) ?? new Map<string, number>();
        for (const s of extra) m.set(s, (m.get(s) ?? 0) + 1);
        this.streams.set(id, m);
      }
    }
    this.scheduleSubFlush();
    let done = false;
    return () => {
      if (done) return;
      done = true;
      for (const id of uniq) {
        const n = (this.subs.get(id) ?? 1) - 1;
        if (n <= 0) this.subs.delete(id);
        else this.subs.set(id, n);
        const m = this.streams.get(id);
        if (m) {
          for (const s of extra) {
            const k = (m.get(s) ?? 1) - 1;
            if (k <= 0) m.delete(s);
            else m.set(s, k);
          }
          if (!m.size) this.streams.delete(id);
        }
      }
      this.scheduleSubFlush();
    };
  }

  private streamKey(id: string): string {
    return [...(this.streams.get(id)?.keys() ?? [])].sort().join(",");
  }

  subscribedIds(): string[] {
    return [...this.subs.keys()];
  }

  private scheduleSubFlush() {
    if (this.subFlushTimer) return;
    this.subFlushTimer = setTimeout(() => {
      this.subFlushTimer = null;
      this.flushSubs();
    }, SUB_FLUSH_MS);
  }

  private flushSubs() {
    if (!this.ws || this.ws.readyState !== OPEN) return;
    const remove = [...this.sent.keys()].filter((id) => !this.subs.has(id));
    // 新订阅，或数据流集合变了（服务端对再次 subscribe 的机器人整体替换数据流）；按数据流集合分组发
    const groups = new Map<string, string[]>();
    for (const id of this.subs.keys()) {
      const key = this.streamKey(id);
      if (this.sent.get(id) === key) continue;
      groups.set(key, [...(groups.get(key) ?? []), id]);
    }
    // 先退订再订阅：服务端每会话有上限，换一批时先腾位置
    if (remove.length && this.raw({ t: "unsubscribe", p: { robot_ids: remove } }))
      remove.forEach((id) => this.sent.delete(id));
    for (const [key, ids] of groups) {
      const p = key ? { robot_ids: ids, streams: key.split(",") } : { robot_ids: ids };
      if (this.raw({ t: "subscribe", p })) ids.forEach((id) => this.sent.set(id, key));
    }
  }

  // ---------------- 指令 ----------------

  /** 速度：不等回执；连接不可用返回 false（调用方应停止发送并提示）。 */
  sendVelocity(robotId: string, vx: number, vy: number, wz: number): boolean {
    if ((this.ws?.bufferedAmount ?? 0) > VELOCITY_MAX_BUFFERED) return false;
    return this.raw({
      t: "cmd",
      p: { robot_id: robotId, request_id: null, cmd: { t: "velocity", p: { vx, vy, wz, level: 0, ttl_ms: LIMITS.VELOCITY_TTL_MS } } },
    });
  }

  /** 非速度指令：等回执。拒绝/失败/超时都以 `CommandError` 抛出。 */
  sendCommand(robotId: string, cmd: Command, timeoutMs = CMD_TIMEOUT_MS): Promise<AckResult> {
    const requestId = uuid();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(requestId);
        reject(new CommandError("timeout"));
      }, timeoutMs);
      this.pending.set(requestId, { resolve, reject, timer });
      if (!this.raw({ t: "cmd", p: { robot_id: robotId, request_id: requestId, cmd } })) {
        clearTimeout(timer);
        this.pending.delete(requestId);
        reject(new CommandError("offline"));
      }
    });
  }

  /** 二进制帧（远程说话 kind 5）：不等回执；连接不可用或积压太多返回 false（这一帧丢掉）。 */
  sendBinary(data: ArrayBuffer): boolean {
    const ws = this.ws;
    if (!ws || ws.readyState !== OPEN || ws.bufferedAmount > TALK_MAX_BUFFERED) return false;
    ws.send(data);
    return true;
  }

  leaseHeartbeat(robotId: string, leaseId: string): boolean {
    return this.raw({ t: "lease_heartbeat", p: { robot_id: robotId, lease_id: leaseId } });
  }
}

export function consoleUrl(ticket: string): string {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${location.host}/v1/ws/console?ticket=${encodeURIComponent(ticket)}`;
}

export const isAuthClose = (code: number | null) => code === CLOSE_CODES.AUTH_FAILED;
